import { BaseService } from "./BaseService";
import { tenantHeadersOptions } from "@/utils/hotelSubscriptionAccess";
import {
  IFileService,
  FileUploadDto,
  FileUploadResponse,
} from "./interfaces/IFileService";

class FileService extends BaseService implements IFileService {
  private basePath = "/files";

  /**
   * `tenantId` (tenant mode, platform admins) stores the file in that tenant via
   * `x-tenant-id`, e.g. the logo of a hotel the platform creates (DV-168).
   */
  async requestUpload(
    fileData: FileUploadDto,
    tenantId?: string,
  ): Promise<FileUploadResponse> {
    return this.post<FileUploadResponse>(
      `${this.basePath}/request-upload`,
      fileData,
      tenantHeadersOptions(tenantId),
    );
  }
  async getSignedUrl(fileId: string, tenantId?: string): Promise<string> {
    return this.get<string>(
      `${this.basePath}/${fileId}`,
      undefined,
      tenantId ? { headers: { "x-tenant-id": tenantId } } : undefined,
    );
  }

  async getTenantLogoSignedUrl(): Promise<string> {
    return this.get<string>("/tenant/logo");
  }
  async requestUpdate(fileId: string): Promise<string> {
    return this.post<string>(`${this.basePath}/request-update/${fileId}`);
  }
  async markAsUploaded(fileId: string, tenantId?: string): Promise<void> {
    return this.post<void>(
      `${this.basePath}/mark-uploaded/${fileId}`,
      undefined,
      tenantHeadersOptions(tenantId),
    );
  }

  async uploadFile(uploadUrl: string, file: File): Promise<any> {
    return this.uploadFileWithContentType(uploadUrl, file, file.type);
  }

  async uploadFileWithContentType(
    uploadUrl: string,
    file: File,
    contentType: string,
  ): Promise<any> {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": contentType,
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Unknown error");
      throw new Error(
        `Failed to upload file: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }
  }

  async uploadFileWithPresignedUrl(
    file: File,
    tenantId?: string,
  ): Promise<string> {
    const fileData: FileUploadDto = {
      filename: file.name,
      filesize: file.size,
    };

    const uploadResponse = await this.requestUpload(fileData, tenantId);
    await this.uploadFile(uploadResponse.signedUrl, file);
    await this.markAsUploaded(uploadResponse.fileId, tenantId);
    return uploadResponse.fileId;
  }

  async updateFileWithPresignedUrl(
    fileId: string,
    file: File,
  ): Promise<string> {
    const signedUrl = await this.requestUpdate(fileId);
    await this.uploadFile(signedUrl, file);
    return fileId;
  }
}

export const fileService = new FileService();
